/* @flow */
import moment from 'moment';

import navigation from 'services/NavigationService';

import { apiService } from 'services/ServiceRegistry';
import { activities } from 'components/Activity/ActivityData.js';

import Form from '../../_.extend';
import type { AssetCaaSFields } from '../../../types/asset.js';

export const makeFields: Function = (assetData: Object, mode: string, t: Function) => {
  return [
    {
      // CHOOSE ACCOUNT
      name: 'chooseAccount',
      label: assetData.id ? t('general:trading_account') : t('general:actions.choose_account'),
      fields: [
        {
          name: 'tradingAccount',
          label: t('general:actions.choose_trading_account'),
          rules: 'required',
          value: assetData && assetData.account ? assetData.account.name : null,
          extra: {
            selectID: assetData && assetData.account ? assetData.account.id : null,
          },
        },
      ],
    },
    {
      // ASSET DETAIL
      name: 'assetDetail',
      label: t('general:assets.labels.details'),
      fields: [
        {
          name: 'assetNumber',
          label: t('general:assets.labels.number'),
          rules: mode !== 'edit' || assetData.owned ? 'required|string' : 'string',
          value: assetData ? assetData.asset_number : null,
        },
        {
          name: 'barcodeNumber',
          label: t('general:labels.barcode_number'),
          rules: 'string',
          value: assetData ? assetData.barcode_number : null,
        },
        {
          name: 'manufacturer',
          label: t('general:manufacturer'),
          rules: mode !== 'edit' || assetData.owned ? 'required|string' : 'string',
          value: assetData && assetData.manufacturer ? assetData.manufacturer : null,
          extra: {
            selectID: assetData && assetData.manufacturer_id ? assetData.manufacturer_id : null,
            parentId: assetData && assetData.account ? assetData.account.id : null,
            actionButton: true,
            setSelectedFields: [
              {
                field: 'model',
                value: null,
              },
              {
                field: 'modelDescription',
                value: null,
              },
            ],
            childExtraFilters: [
              {
                type: 'formField',
                formName: 'assetDetail',
                formField: 'manufacturer',
                isExtra: true,
                value: 'selectID',
              },
              {
                type: 'formFieldParam',
                formName: 'chooseAccount',
                formField: 'tradingAccount',
                isExtra: true,
                value: 'selectID',
                paramName: '?account_id',
              },
            ],
          },
        },
        {
          name: 'model',
          label: t('general:model'),
          rules: mode !== 'edit' || assetData.owned ? 'required|string' : 'string',
          value: assetData && assetData.product_id ? assetData.product_id : null,
          extra: {
            selectID: assetData && assetData.product_id ? assetData.product_id : null,
            parentId:
              assetData && assetData.manufacturer_id
                ? `${assetData.manufacturer_id}` + `?account_id=${assetData.account.id}`
                : null,
            actionButton: true,
            setSelectedFields: [
              {
                field: 'model',
                value: 'model',
              },
              {
                field: 'modelDescription',
                value: 'product_group_description',
              },
            ],
          },
        },
        {
          name: 'modelDescription',
          label: t('general:assets.labels.model_description'),
          rules: 'string',
          value: assetData && assetData.description ? assetData.description : null,
        },
        {
          name: 'serialNumber',
          label: t('general:labels.serial_number'),
          rules: mode !== 'edit' || assetData.owned ? 'required|string' : 'string',
          value: assetData ? assetData.serial_number : null,
        },
        {
          name: 'optionsFitted',
          label: t('general:assets.labels.options_fitted'),
          rules: 'string',
          value:
            assetData && assetData.owned_options_fitted_text
              ? assetData.owned_options_fitted_text
              : null,
        },
        {
          name: 'currentCondition',
          label: t('general:assets.labels.current_condition'),
          rules: mode !== 'edit' || assetData.owned ? 'required|string' : 'string',
          value: assetData ? assetData.current_condition : null,
          options: [
            {
              id: 'Excellent',
              name: 'Excellent',
            },
            {
              id: 'Good',
              name: 'Good',
            },
            {
              id: 'Average',
              name: 'Average',
            },
            {
              id: 'Poor',
              name: 'Poor',
            },
            {
              id: 'Scrap',
              name: 'Scrap',
            },
          ],
        },
        {
          name: 'assetStatus',
          label: t('general:assets.labels.status'),
          rules: mode !== 'edit' || assetData.owned ? 'required|string' : 'string',
          value: assetData ? assetData.status : null,
          options: [
            {
              id: 'Not In Use',
              name: 'Not In Use',
            },
            {
              id: 'In Use',
              name: 'In Use',
            },
            {
              id: 'Storage',
              name: 'Storage',
            },
            {
              id: 'Faulty',
              name: 'Faulty',
            },
            {
              id: 'Internal Repair',
              name: 'Internal Repair',
            },
            {
              id: 'External Repair',
              name: 'External Repair',
            },
            {
              id: 'Beyond Economical Repair',
              name: 'Beyond Economical Repair',
            },
            {
              id: 'Internal Calibration',
              name: 'Internal Calibration',
            },
            {
              id: 'External Calibration',
              name: 'External Calibration',
            },
            {
              id: 'Disposed',
              name: 'Disposed',
            },
            {
              id: 'Lost',
              name: 'Lost',
            },
          ],
        },
        {
          name: 'softwareVersion',
          label: t('general:assets.labels.software_version'),
          rules: 'string',
          value: assetData ? assetData.software_version : null,
        },
        {
          name: 'firmwareVersion',
          label: t('general:assets.labels.firmware_version'),
          rules: 'string',
          value: assetData ? assetData.firmware_version : null,
        },
        {
          name: 'purchasedDate',
          label: t('general:assets.labels.purchase_date'),
          rules: 'date',
          value:
            assetData && assetData.purchased_date
              ? moment(assetData.purchased_date, 'YYYY-MM-DD').toDate()
              : '',
        },
      ],
    },
    {
      // Calibration as a Service
      name: 'caas',
      label: t('general:caas.labels.caas'),
      fields: [
        {
          name: 'is_caas_asset',
          label: t('general:caas.labels.caas_asset'),
          value: assetData.is_caas_asset,
        },
        {
          name: 'is_rental_backfill',
          label: t('general:calibrations.labels.rental_backfill'),
          value: assetData.calibration_service?.is_rental_backfill || false,
        },
        {
          name: 'vendor',
          label: t('general:vendor'),
          value: assetData.calibration_service?.vendor || null,
          options: [
            { id: 'keysight', name: 'Keysight Technologies' },
            { id: 'other', name: 'Other' },
          ],
        },
        {
          name: 'type',
          label: t('general:calibrations.labels.type'),
          value: assetData.calibration_service?.type || null,
          options: [
            { id: 'standard', name: 'Standard' },
            { id: 'z540-1', name: 'Z540-1' },
            { id: 'cal_uncertainties', name: 'Cal + Uncertainties' },
            { id: 'cal_uncertainties_guardbanding', name: 'Cal + Uncertainties + Guardbanding' },
            { id: 'standards_lab', name: 'Standards Lab' },
          ],
        },
        {
          name: 'currency',
          label: t('general:calibrations.labels.currency'),
          value: assetData.calibration_service?.currency || null,
          options: [{ id: 'usd', name: 'USD' }, { id: 'gbp', name: 'GBP' }],
        },
        {
          name: 'price',
          label: t('general:calibrations.labels.price'),
          value: assetData.calibration_service?.price || null,
        },
        {
          name: 'observations',
          label: t('general:observations'),
          value: assetData.calibration_service?.observations,
        },
      ],
    },
    {
      // Custom Fields
      name: 'customFields',
      label: t('general:assets.labels.custom_fields'),
      fields: [
        {
          name: 'customField1',
          label:
            assetData &&
            assetData.custom_fields &&
            assetData.custom_fields[0] &&
            assetData.custom_fields[0].label,
          rules: 'string',
          value:
            assetData &&
            assetData.custom_fields &&
            assetData.custom_fields[0] &&
            assetData.custom_fields[0].asset_custom_value,
          extra: {
            selectID:
              assetData &&
              assetData.custom_fields &&
              assetData.custom_fields[0] &&
              assetData.custom_fields[0].custom_field_id,
            helperText:
              assetData &&
              assetData.custom_fields &&
              assetData.custom_fields[0] &&
              assetData.custom_fields[0].text_helper,
          },
        },
        {
          name: 'customField2',
          label:
            assetData &&
            assetData.custom_fields &&
            assetData.custom_fields[1] &&
            assetData.custom_fields[1].label,
          rules: 'string',
          value:
            assetData &&
            assetData.custom_fields &&
            assetData.custom_fields[1] &&
            assetData.custom_fields[1].asset_custom_value,
          extra: {
            selectID:
              assetData &&
              assetData.custom_fields &&
              assetData.custom_fields[1] &&
              assetData.custom_fields[1].custom_field_id,
            helperText:
              assetData &&
              assetData.custom_fields &&
              assetData.custom_fields[1] &&
              assetData.custom_fields[1].text_helper,
          },
        },
        {
          name: 'customField3',
          label:
            assetData &&
            assetData.custom_fields &&
            assetData.custom_fields[2] &&
            assetData.custom_fields[2].label,
          rules: 'string',
          value:
            assetData &&
            assetData.custom_fields &&
            assetData.custom_fields[2] &&
            assetData.custom_fields[2].asset_custom_value,
          extra: {
            selectID:
              assetData &&
              assetData.custom_fields &&
              assetData.custom_fields[2] &&
              assetData.custom_fields[2].custom_field_id,
            helperText:
              assetData &&
              assetData.custom_fields &&
              assetData.custom_fields[2] &&
              assetData.custom_fields[2].text_helper,
          },
        },
        {
          name: 'customField4',
          label:
            assetData &&
            assetData.custom_fields &&
            assetData.custom_fields[3] &&
            assetData.custom_fields[3].label,
          rules: 'string',
          value:
            assetData &&
            assetData.custom_fields &&
            assetData.custom_fields[3] &&
            assetData.custom_fields[3].asset_custom_value,
          extra: {
            selectID:
              assetData &&
              assetData.custom_fields &&
              assetData.custom_fields[3] &&
              assetData.custom_fields[3].custom_field_id,
            helperText:
              assetData &&
              assetData.custom_fields &&
              assetData.custom_fields[3] &&
              assetData.custom_fields[3].text_helper,
          },
        },
      ],
    },
    {
      // CALIBRATION DETAILS
      name: 'calibrationDetails',
      label: t('general:calibrations.labels.details'),
      fields: [
        {
          name: 'calibrationCertificate',
          label: t('general:calibrations.certificate'),
          // rules: 'file_extension:pdf|max_file_size',
          value: assetData && assetData.calibration ? assetData.calibration.certificate : null,
        },
        {
          name: 'calibrationStatus',
          label: t('general:calibrations.labels.status'),
          rules: mode !== 'edit' || assetData.owned ? 'required|string' : 'string',
          value: assetData && assetData.calibration ? assetData.calibration.status : null,
          options: [
            {
              id: 'In Cal',
              name: 'In Cal',
            },
            {
              id: 'Out of Cal',
              name: 'Out of Cal',
            },
            {
              id: 'Not Required',
              name: 'Not Required',
            },
          ],
        },
        {
          name: 'lastCalibrated',
          label: t('general:calibrations.labels.last_calibrated'),
          rules: 'date',
          value:
            assetData &&
            assetData.calibration &&
            assetData.calibration.date &&
            assetData.calibration.date !== '0000-00-00 00:00:00'
              ? moment(assetData.calibration.date, 'YYYY-MM-DD').toDate()
              : '',
        },
        {
          name: 'calibrationDueDate',
          label: t('general:calibrations.labels.due_date'),
          rules: 'date',
          value:
            assetData &&
            assetData.calibration &&
            assetData.calibration.due_date &&
            assetData.calibration.due_date !== '0000-00-00 00:00:00'
              ? moment(assetData.calibration.due_date, 'YYYY-MM-DD').toDate()
              : '',
        },
        {
          name: 'calibrationPlannedDate',
          label: t('general:calibrations.labels.planned_date'),
          rules: 'date',
          value:
            assetData &&
            assetData.calibration &&
            assetData.calibration.planned_date &&
            assetData.calibration.planned_date !== '0000-00-00 00:00:00'
              ? moment(assetData.calibration.planned_date, 'YYYY-MM-DD').toDate()
              : '',
        },
        {
          name: 'calibrationInterval',
          label: t('general:calibrations.labels.interval'),
          rules: '',
          value: assetData && assetData.calibration ? assetData.calibration.interval : null,
          options: [
            {
              id: 0,
              name: 'Weekly',
            },
            {
              id: 1,
              name: 'Monthly',
            },
            {
              id: 2,
              name: 'Annually',
            },
          ],
        },
        {
          name: 'inServiceDate',
          label: t('general:assets.labels.in_service_date'),
          rules: 'date',
          value:
            assetData &&
            assetData.calibration &&
            assetData.calibration.in_service_date &&
            assetData.calibration.in_service_date !== '0000-00-00 00:00:00'
              ? moment(assetData.calibration.in_service_date, 'YYYY-MM-DD').toDate()
              : '',
        },
        {
          name: 'safetyTested',
          label: t('general:assets.labels.safety_tested'),
          // rules: 'string',
          options: [
            {
              id: 'Yes',
              label: 'Yes',
              value: 'Yes',
            },
            {
              id: 'No',
              label: 'No',
              value: 'No',
            },
          ],
          value:
            assetData && assetData.calibration && assetData.calibration.safety_tested === 'Yes'
              ? 'Yes'
              : 'No',
        },
      ],
    },
    {
      name: 'location',
      label: assetData.id
        ? t('general:actions.change_location')
        : t('general:assets.labels.location'),
      fields: [
        {
          name: 'countryId',
          value:
            assetData && assetData.location && assetData.location.site
              ? assetData.location.site.country_id
              : null,
          rules: 'string',
        },
        {
          name: 'locationSiteId',
          value: '',
          rules: 'string',
        },
        {
          name: 'locationCompany',
          label: t('general:company'),
          rules: 'required|string',
          value:
            assetData && assetData.location && assetData.location.company
              ? assetData.location.company.name
              : null,
          extra: {
            selectID:
              assetData && assetData.location && assetData.location.company
                ? assetData.location.company.id
                : null,
            parentId: assetData && assetData.account ? assetData.account.id : null,
            actionButton: true,
            setSelectedFields: [
              {
                field: 'locationSite',
                value: null,
              },
              {
                field: 'locationBuilding',
                value: null,
              },
              {
                field: 'locationRoom',
                value: null,
              },
              {
                field: 'locationSystem',
                value: null,
              },
            ],
          },
        },
        {
          name: 'locationSite',
          label: t('general:locations.labels.site'),
          rules: 'required|string',
          value:
            assetData && assetData.location && assetData.location.site
              ? assetData.location.site.name
              : null,
          extra: {
            selectID:
              assetData && assetData.location && assetData.location.site
                ? assetData.location.site.id
                : null,
            parentId:
              assetData &&
              assetData.location &&
              assetData.location.company &&
              assetData.location.company.id,
            actionButton: true,
            setSelectedFields: [
              {
                field: 'countryId',
                value: 'country_id',
              },
              {
                field: 'locationBuilding',
                value: null,
              },
              {
                field: 'locationRoom',
                value: null,
              },
              {
                field: 'locationSystem',
                value: null,
              },
            ],
            childExtraFilters: [
              {
                type: 'formField',
                formName: 'location',
                formField: 'locationCompany',
                isExtra: true,
                value: 'selectID',
              },
              {
                type: 'formField',
                formName: 'location',
                formField: 'countryId',
                isExtra: false,
                value: 'value',
              },
              {
                type: 'string',
                value: '?parent_id=',
              },
            ],
          },
        },
        {
          name: 'locationBuilding',
          label: t('general:locations.labels.building_transport'),
          rules: 'string',
          value:
            assetData && assetData.location && assetData.location.building
              ? assetData.location.building.name
              : null,
          extra: {
            selectID:
              assetData && assetData.location && assetData.location.building
                ? assetData.location.building.id
                : null,
            parentId:
              assetData && assetData.location && assetData.location.building
                ? `/${assetData.location.company.id}` +
                  `/${assetData.location.site.country_id}` +
                  `?parent_id=${assetData.location.site.id}`
                : null,
            actionButton: true,
            setSelectedFields: [
              {
                field: 'locationRoom',
                value: null,
              },
              {
                field: 'locationSystem',
                value: null,
              },
            ],
            childExtraFilters: [
              {
                type: 'formField',
                formName: 'location',
                formField: 'locationCompany',
                isExtra: true,
                value: 'selectID',
              },
              {
                type: 'formField',
                formName: 'location',
                formField: 'countryId',
                isExtra: false,
                value: 'value',
              },
              {
                type: 'string',
                value: '?parent_id=',
              },
            ],
          },
        },
        {
          name: 'locationRoom',
          label: t('general:locations.labels.lab_room'),
          rules: 'string',
          value:
            assetData && assetData.location && assetData.location.room
              ? assetData.location.room.name
              : null,
          extra: {
            selectID:
              assetData && assetData.location && assetData.location.room
                ? assetData.location.room.id
                : null,
            parentId:
              assetData && assetData.location && assetData.location.room
                ? `/${assetData.location.company.id}` +
                  `/${assetData.location.site.country_id}` +
                  `?parent_id=${assetData.location.building.id}`
                : null,
            actionButton: true,
            setSelectedFields: [
              {
                field: 'locationSystem',
                value: null,
              },
            ],
            childExtraFilters: [
              {
                type: 'formField',
                formName: 'location',
                formField: 'locationCompany',
                isExtra: true,
                value: 'selectID',
              },
              {
                type: 'formField',
                formName: 'location',
                formField: 'countryId',
                isExtra: false,
                value: 'value',
              },
              {
                type: 'string',
                value: '?parent_id=',
              },
            ],
          },
        },
        {
          name: 'locationSystem',
          label: t('general:locations.labels.system_workspace'),
          rules: 'string',
          value:
            assetData && assetData.location && assetData.location.workspace
              ? assetData.location.workspace.name
              : null,
          extra: {
            selectID:
              assetData && assetData.location && assetData.location.workspace
                ? assetData.location.workspace.id
                : null,
            parentId:
              assetData && assetData.location && assetData.location.workspace
                ? `/${assetData.location.company.id}` +
                  `/${assetData.location.site.country_id}` +
                  `?parent_id=${assetData.location.room.id}`
                : null,
            actionButton: true,
          },
        },
        {
          name: 'streetNumber',
          label: t('general:labels.street_number'),
          rules: 'string',
          value: '',
        },
        {
          name: 'town',
          label: t('general:town_city'),
          rules: 'string',
          value: '',
        },
        {
          name: 'state',
          label: t('general:state_county_province'),
          rules: 'string',
          value: '',
        },
        {
          name: 'postCode',
          label: t('general:zip_postcode'),
          rules: 'string',
          value: '',
        },
        {
          name: 'country',
          label: t('general:country'),
          rules: 'string',
          value: '',
        },
        {
          name: 'phone',
          label: t('general:labels.contact_phone_number'),
          rules: 'telephone',
          value: '',
        },
        {
          name: 'fax',
          label: t('general:labels.contact_fax_number'),
          rules: 'telephone',
          value: '',
        },
      ],
    },
    {
      // ASSET RELATIONSHIP
      name: 'assetRelationship',
      label: t('general:asset_relationship'),
      fields: [
        {
          key: 'relationship',
          type: 'radiogroup',
          title: '',
          name: 'relationship',
          label: '',
          rules: 'string',
          value: assetData ? assetData.asset_relationship : null,
          options: [
            {
              id: 'parent',
              label: t('general:assets.parent'),
              value: 'parent',
              hint: t('general:assets.descriptions.parent'),
            },
            {
              id: 'no parent',
              label: t('general:assets.no_parent'),
              value: 'no parent',
              hint: t('general:assets.descriptions.no_parent'),
            },
            {
              id: 'internal',
              label: t('general:assets.internal'),
              value: 'internal',
              hint: t('general:assets.descriptions.internal'),
            },
            {
              id: 'normal',
              label: t('general:assets.normal'),
              value: 'normal',
              hint: t('general:assets.descriptions.normal'),
            },
          ],
        },
        {
          name: 'parentAsset',
          label: t('general:actions.choose_parent_asset'),
          rules: 'string',
          value: assetData && assetData.parent ? assetData.parent.asset_number : null,
          extra: {
            selectID: assetData && assetData.parent ? assetData.parent.id : null,
            parentId: assetData && assetData.account ? assetData.account.id : null,
          },
        },
      ],
    },
    {
      // ASSIGN ASSET
      name: 'assignAsset',
      label: assetData.id ? t('general:actions.reassign_asset') : t('general:actions.assign_asset'),
      fields: [
        {
          name: 'assignee',
          label: t('general:actions.choose_asset_assignee'),
          rules: 'string',
          value: assetData && assetData.user ? assetData.user.id : null,
          extra: {
            selectID: assetData && assetData.user ? assetData.user.id : null,
            parentId: assetData && assetData.account ? assetData.account.id : null,
          },
        },
      ],
    },
    {
      // ASSET AVAILABLE FOR
      name: 'assetAvailableFor',
      label: t('general:assets.labels.available_for'),
      fields: [
        {
          name: 'saleConsignment',
          label: t('general:assets.labels.sale_consignment'),
          rules: '',
          value: assetData ? assetData.sale_consignment : null,
        },
        {
          name: 'subrentalProgram',
          label: t('general:assets.labels.sub_rental_program'),
          rules: '',
          value: assetData ? assetData.subrental_program : null,
        },
      ],
    },
    {
      name: 'financialAudit',
      label: t('general:assets.labels.financial_audit'),
      fields: [
        {
          name: 'dateOfPurchase',
          label: t('general:labels.date_of_purchase'),
          rules: 'date',
          value:
            assetData && assetData.financial_audit && assetData.financial_audit.date_of_purchase
              ? moment(assetData.financial_audit.date_of_purchase, 'YYYY-MM-DD').toDate()
              : null,
        },
        {
          name: 'financialAccountNo',
          label: t('general:accounts.labels.number'),
          rules: 'string',
          value:
            assetData && assetData.financial_audit
              ? assetData.financial_audit.financial_account_no
              : null,
        },
        {
          name: 'departmentCode',
          label: t('general:assets.labels.department_code'),
          rules: 'string',
          value:
            assetData && assetData.financial_audit
              ? assetData.financial_audit.department_code
              : null,
        },
        {
          name: 'assetInServiceDate',
          label: t('general:assets.labels.in_service_date'),
          rules: 'date',
          value:
            assetData && assetData.financial_audit && assetData.financial_audit.in_service_date
              ? moment(assetData.financial_audit.in_service_date, 'YYYY-MM-DD').toDate()
              : null,
        },
        {
          name: 'lifeCyclePeriod',
          label: t('general:assets.labels.life_cycle_period'),
          rules: 'numeric',
          value:
            assetData && assetData.financial_audit
              ? assetData.financial_audit.life_cycle_period_months
              : null,
        },
        {
          name: 'supplier',
          label: t('general:labels.supplier'),
          rules: 'string',
          value: assetData && assetData.financial_audit ? assetData.financial_audit.supplier : null,
        },
        {
          name: 'poNumber',
          label: t('general:labels.po_number'),
          rules: 'numeric',
          value:
            assetData && assetData.financial_audit ? assetData.financial_audit.er_po_number : null,
        },
        {
          name: 'purchasePrice',
          label: t('general:assets.labels.purchase_price'),
          rules: 'numeric',
          value: assetData && assetData.financial_audit ? assetData.financial_audit.price : null,
        },
        {
          name: 'netBookValue',
          label: t('general:labels.net_book_value'),
          rules: 'numeric',
          value:
            assetData && assetData.financial_audit
              ? assetData.financial_audit.net_book_value
              : null,
        },
        {
          name: 'fairMarketValue',
          label: t('general:labels.fair_market_value'),
          rules: 'numeric',
          value:
            assetData && assetData.financial_audit
              ? assetData.financial_audit.fair_market_value
              : null,
        },
        {
          name: 'currency',
          label: t('general:currency'),
          rules: 'string',
          value: assetData && assetData.financial_audit ? assetData.financial_audit.currency : null,
        },
        {
          name: 'warrantyPeriod',
          label: t('general:assets.labels.warranty_period'),
          rules: 'numeric',
          value:
            assetData && assetData.financial_audit
              ? assetData.financial_audit.warranty_period_months
              : null,
        },
        {
          name: 'warrantyExpires',
          label: t('general:assets.labels.warranty_expires'),
          rules: 'date',
          value:
            assetData && assetData.financial_audit && assetData.financial_audit.warranty_expires
              ? moment(assetData.financial_audit.warranty_expires, 'YYYY-MM-DD').toDate()
              : null,
        },
        {
          name: 'disposalDate',
          label: t('general:assets.labels.disposal_date'),
          rules: 'date',
          value:
            assetData && assetData.financial_audit && assetData.financial_audit.disposal_date
              ? moment(assetData.financial_audit.disposal_date, 'YYYY-MM-DD').toDate()
              : null,
        },
        {
          name: 'disposalPrice',
          label: t('general:assets.labels.disposal_price'),
          rules: 'numeric',
          value:
            assetData && assetData.financial_audit
              ? assetData.financial_audit.disposal_price
              : null,
        },
        {
          name: 'disposalCurrency',
          label: t('general:assets.labels.disposal_currency'),
          rules: 'string',
          value:
            assetData && assetData.financial_audit
              ? assetData.financial_audit.disposal_currency
              : null,
        },
        {
          name: 'auditedBy',
          label: t('general:assets.labels.audited_by'),
          rules: '',
          value:
            assetData && assetData.financial_audit ? assetData.financial_audit.audited_by : null,
        },
      ],
    },
    {
      // ADDITIONAL INFORMATION
      name: 'additionalInformation',
      label: t('general:additional_info'),
      fields: [
        {
          name: 'comments',
          label: t('general:labels.comments'),
          rules: 'string',
          value: assetData ? assetData.additional_info : null,
        },
      ],
    },
  ];
};

class SingleAssetFormCreate extends Form {}

class SingleAssetFormEdit extends Form {}

export const assetCreate: Function = (fields: Array<*>, localState: Object, t: Function) => {
  const hooks: Object = {
    onSubmit(form: Object) {
      if (form.values().calibrationDetails.calibrationStatus === 'Not Required') {
        form.set('value', {
          calibrationDetails: {
            lastCalibrated: null,
            calibrationDueDate: null,
            calibrationInterval: null,
            calibrationPlannedDate: null,
            inServiceDate: null,
            safetyTested: '',
          },
        });
      }
    },
    // eslint error - Maximum allowed is 40  max-statements
    // eslint-disable-next-line
    onSuccess(form: Object) {
      localState.isLoading = true;
      localState.responseMessage = t('general:messages.saving_form');
      localState.showResponseMessage = true;
      localState.hasError = false;
      localState.hasSuccess = true;

      const {
        assetNumber,
        barcodeNumber,
        model,
        modelDescription,
        serialNumber,
        optionsFitted,
        currentCondition,
        assetStatus,
        softwareVersion,
        firmwareVersion,
        purchasedDate,
      }: Object = form.values().assetDetail;

      const {
        customField1,
        customField2,
        customField3,
        customField4,
      }: Object = form.values().customFields;

      const {
        calibrationCertificate,
        calibrationStatus,
        lastCalibrated,
        calibrationDueDate,
        calibrationInterval,
        calibrationPlannedDate,
        inServiceDate,
        safetyTested,
      }: Object = form.values().calibrationDetails;

      const vLocationId: string =
        form.$('location.locationSystem').extra.selectID ||
        form.$('location.locationRoom').extra.selectID ||
        form.$('location.locationBuilding').extra.selectID ||
        form.$('location.locationSite').extra.selectID ||
        form.$('location.locationCompany').extra.selectID;

      const { assignee }: Object = form.values().assignAsset;

      const { relationship }: Object = form.values().assetRelationship;

      const { saleConsignment, subrentalProgram }: Object = form.values().assetAvailableFor;

      const { comments }: Object = form.values().additionalInformation;

      const vCustomFields: Array = [];
      customField1 &&
        vCustomFields.push({
          custom_field_id: form.$('customFields.customField1').extra.selectID,
          asset_custom_value: customField1,
        });
      customField2 &&
        vCustomFields.push({
          custom_field_id: form.$('customFields.customField2').extra.selectID,
          asset_custom_value: customField2,
        });
      customField3 &&
        vCustomFields.push({
          custom_field_id: form.$('customFields.customField3').extra.selectID,
          asset_custom_value: customField3,
        });
      customField4 &&
        vCustomFields.push({
          custom_field_id: form.$('customFields.customField4').extra.selectID,
          asset_custom_value: customField4,
        });

      const jsonObj: Object = {
        ...getCaaSFields(form),
        activity_tag: activities.createdAsset.tag, // eslint-disable-line camelcase
        account_id: form.$('chooseAccount.tradingAccount').extra.selectID,
        asset_number: assetNumber,
        barcode_number: barcodeNumber,
        owned_manufacturer_id: form.$('assetDetail.manufacturer').extra.selectID,
        owned_product_id: form.$('assetDetail.model').extra.selectID,
        model,
        description: modelDescription,
        er_serial_number: serialNumber,
        owned_options_fitted_text: optionsFitted,
        parent_id:
          relationship === 'internal'
            ? form.$('assetRelationship.parentAsset').extra.selectID
            : null,
        current_condition: currentCondition,
        overall_status: assetStatus,
        software_version: softwareVersion,
        firmware_version: firmwareVersion,
        purchased_date: purchasedDate,
        custom_fields: vCustomFields,
        calibration_certificate: calibrationCertificate,
        er_calibration_status: calibrationStatus,
        er_calibration_interval: calibrationInterval,
        er_calibration_date: moment(lastCalibrated).format('YYYY-MM-DD'),
        er_calibration_due_date: moment(calibrationDueDate).format('YYYY-MM-DD'),
        er_calibration_in_service_date: moment(inServiceDate).format('YYYY-MM-DD'),
        planned_calibration_date: moment(calibrationPlannedDate).format('YYYY-MM-DD'),
        calibration_safety_tested: Boolean(safetyTested.toLowerCase() === 'yes'),
        location_id: vLocationId,
        asset_relationship: relationship,
        user_id: form.$('assignAsset.assignee').extra.selectID,
        sale_consignment: Boolean(saleConsignment),
        subrental_program: Boolean(subrentalProgram),
        additional_info: comments,
      };

      if (relationship === 'internal' && !form.$('assetRelationship.parentAsset').extra.selectID) {
        localState.isLoading = false;
        localState.responseMessage = t('general:assets.messages.parent_asset_required');
        localState.showResponseMessage = true;
        localState.hasSuccess = false;
        localState.hasError = true;
        return;
      }

      !barcodeNumber && delete jsonObj.barcode_number;
      !calibrationCertificate && delete jsonObj.calibration_certificate;
      !comments && delete jsonObj.additional_info;
      !calibrationInterval && delete jsonObj.er_calibration_interval;
      !firmwareVersion && delete jsonObj.firmware_version;
      !softwareVersion && delete jsonObj.software_version;
      !saleConsignment && delete jsonObj.sale_consignment;
      !subrentalProgram && delete jsonObj.subrental_program;
      !assignee && delete jsonObj.user_id;
      !lastCalibrated && delete jsonObj.er_calibration_date;
      !calibrationDueDate && delete jsonObj.er_calibration_due_date;
      !calibrationPlannedDate && delete jsonObj.planned_calibration_date;
      !inServiceDate && delete jsonObj.er_calibration_in_service_date;
      !purchasedDate && delete jsonObj.purchased_date;
      vCustomFields.length === 0 && delete jsonObj.custom_fields;

      apiService
        .createResource('assets', jsonObj)
        .then((response: Object) => {
          localState.isLoading = false;
          localState.responseMessage = t('general:messages.save_success');

          setTimeout(() => {
            localState.showResponseMessage = false;
            /*eslint-disable max-nested-callbacks*/
            setTimeout(() => {
              // Snackbar animation 225ms
              navigation.goBack();
            }, 225);
          }, 1200);

          return response;
        })

        .catch((err: string) => {
          localState.isLoading = false;
          localState.responseMessage =
            err && err.errors && err.errors.name && err.errors.name.length > 0
              ? err.errors.name[0]
              : err && err.message;
          localState.hasSuccess = false;
          localState.hasError = true;
        });
    },
    onError(form: Object) {
      localState.isLoading = false;
      localState.responseMessage = t('general:messages.form_error');
      localState.showResponseMessage = true;
      localState.hasSuccess = false;
      localState.hasError = true;
    },
  };

  return new SingleAssetFormCreate(
    { fields },
    { hooks, name: t('general:actions.create_new_asset') }
  );
};

export const assetEdit: Function = (
  fields: Array<*>,
  assetData: Object,
  localState: Object,
  t: Function
) => {
  const hooks: Object = {
    onSubmit(form: Object) {
      if (form.values().calibrationDetails.calibrationStatus === 'Not Required') {
        form.set('value', {
          calibrationDetails: {
            lastCalibrated: null,
            calibrationDueDate: null,
            calibrationInterval: null,
            calibrationPlannedDate: null,
            inServiceDate: null,
            safetyTested: '',
          },
        });
      }
    },
    onSuccess(form: Object) {
      localState.isLoading = true;
      localState.responseMessage = t('general:messages.saving_form');
      localState.showResponseMessage = true;
      localState.hasError = false;
      localState.hasSuccess = true;

      const {
        assetNumber,
        barcodeNumber,
        model,
        modelDescription,
        serialNumber,
        optionsFitted,
        currentCondition,
        assetStatus,
        softwareVersion,
        firmwareVersion,
        purchasedDate,
      }: Object = form.values().assetDetail;

      const {
        customField1,
        customField2,
        customField3,
        customField4,
      }: Object = form.values().customFields;

      const {
        calibrationStatus,
        lastCalibrated,
        calibrationDueDate,
        calibrationInterval,
        calibrationPlannedDate,
        inServiceDate,
        safetyTested,
      }: Object = form.values().calibrationDetails;

      const vLocationId: string =
        form.$('location.locationSystem').extra.selectID ||
        form.$('location.locationRoom').extra.selectID ||
        form.$('location.locationBuilding').extra.selectID ||
        form.$('location.locationSite').extra.selectID ||
        form.$('location.locationCompany').extra.selectID;

      const { relationship }: Object = form.values().assetRelationship;

      const { saleConsignment, subrentalProgram }: Object = form.values().assetAvailableFor;

      const {
        dateOfPurchase,
        financialAccountNo,
        departmentCode,
        assetInServiceDate,
        lifeCyclePeriod,
        supplier,
        poNumber,
        purchasePrice,
        netBookValue,
        fairMarketValue,
        currency,
        warrantyPeriod,
        warrantyExpires,
        disposalDate,
        disposalPrice,
        disposalCurrency,
        auditedBy,
      }: Object = form.values().financialAudit;

      const { comments }: Object = form.values().additionalInformation;

      const vCustomFields: Array = [];
      form.$('customFields.customField1').extra.selectID &&
        vCustomFields.push({
          custom_field_id: form.$('customFields.customField1').extra.selectID,
          asset_custom_value: customField1,
        });
      form.$('customFields.customField2').extra.selectID &&
        vCustomFields.push({
          custom_field_id: form.$('customFields.customField2').extra.selectID,
          asset_custom_value: customField2,
        });
      form.$('customFields.customField3').extra.selectID &&
        vCustomFields.push({
          custom_field_id: form.$('customFields.customField3').extra.selectID,
          asset_custom_value: customField3,
        });
      form.$('customFields.customField4').extra.selectID &&
        vCustomFields.push({
          custom_field_id: form.$('customFields.customField4').extra.selectID,
          asset_custom_value: customField4,
        });

      const jsonObj: Object = {
        ...getCaaSFields(form, assetData),
        activity_tag: activities.edited.tag, // eslint-disable-line camelcase
        account_id: form.$('chooseAccount.tradingAccount').extra.selectID,
        asset_number: assetNumber,
        barcode_number: barcodeNumber,
        owned_manufacturer_id: form.$('assetDetail.manufacturer').extra.selectID,
        owned_product_id: form.$('assetDetail.model').extra.selectID,
        model,
        description: modelDescription,
        er_serial_number: serialNumber,
        owned_options_fitted_text: optionsFitted,
        parent_id:
          relationship === 'internal'
            ? form.$('assetRelationship.parentAsset').extra.selectID
            : null,
        current_condition: currentCondition,
        overall_status: assetStatus,
        software_version: softwareVersion,
        firmware_version: firmwareVersion,
        purchased_date: purchasedDate,
        custom_fields: vCustomFields,
        //'calibration_certificate': calibrationCertificate,
        er_calibration_status: calibrationStatus,
        er_calibration_interval: calibrationInterval,
        er_calibration_date: moment(lastCalibrated).format('YYYY-MM-DD'),
        er_calibration_due_date: moment(calibrationDueDate).format('YYYY-MM-DD'),
        er_calibration_in_service_date: moment(inServiceDate).format('YYYY-MM-DD'),
        planned_calibration_date: moment(calibrationPlannedDate).format('YYYY-MM-DD'),
        calibration_safety_tested: Boolean(safetyTested.toLowerCase() === 'yes'),
        location_id: vLocationId,
        asset_relationship: relationship,
        user_id: form.$('assignAsset.assignee').extra.selectID,
        sale_consignment: Boolean(saleConsignment),
        subrental_program: Boolean(subrentalProgram),
        // Financial Audit
        date_of_purchase: dateOfPurchase,
        financial_account_no: financialAccountNo,
        department_code: departmentCode,
        in_service_date: assetInServiceDate,
        life_cycle_period_months: lifeCyclePeriod,
        supplier,
        er_po_number: poNumber,
        price: purchasePrice,
        net_book_value: netBookValue,
        fair_market_value: fairMarketValue,
        currency,
        warranty_period_months: warrantyPeriod,
        warranty_expires: warrantyExpires,
        disposal_date: disposalDate,
        disposal_price: disposalPrice,
        disposal_currency: disposalCurrency,
        audited_by: auditedBy ? auditedBy.id : null,
        // Additional Info
        additional_info: comments,
      };

      if (relationship === 'internal' && !form.$('assetRelationship.parentAsset').extra.selectID) {
        localState.isLoading = false;
        localState.responseMessage = t('general:assets.messages.parent_asset_required');
        localState.showResponseMessage = true;
        localState.hasSuccess = false;
        localState.hasError = true;
        return;
      }

      // When edit should allow to put empty values
      // !barcodeNumber && delete jsonObj.barcode_number;
      // !calibrationCertificate && delete jsonObj.calibration_certificate;
      // !comments && delete jsonObj.additional_info;
      // !calibrationInterval && delete jsonObj.er_calibration_interval;
      // !firmwareVersion && delete jsonObj.firmware_version;
      // !softwareVersion && delete jsonObj.software_version;
      // !saleConsignment && delete jsonObj.sale_consignment;
      // !subrentalProgram && delete jsonObj.subrental_program;
      // !assignee && delete jsonObj.user_id;
      !lastCalibrated && (jsonObj.er_calibration_date = '');
      !calibrationDueDate && (jsonObj.er_calibration_due_date = '');
      !inServiceDate && (jsonObj.er_calibration_in_service_date = '');
      !purchasedDate && (jsonObj.purchased_date = '');
      !calibrationPlannedDate && (jsonObj.planned_calibration_date = '');
      // vCustomFields.length === 0 && delete jsonObj.custom_fields;

      apiService
        .putResource(`assets/${assetData.id}`, jsonObj)
        .then((response: Object) => {
          localState.isLoading = false;
          localState.responseMessage = t('general:messages.save_success');

          setTimeout(() => {
            localState.showResponseMessage = false;
            /*eslint-disable max-nested-callbacks*/
            setTimeout(() => {
              // Snackbar animation 225ms
              navigation.goBack();
            }, 225);
          }, 1200);

          return response;
        })

        .catch((err: string) => {
          localState.isLoading = false;
          localState.responseMessage =
            err && err.errors && err.errors.name && err.errors.name.length > 0
              ? err.errors.name[0]
              : err && err.message;
          localState.hasSuccess = false;
          localState.hasError = true;
        });
    },
    onError(form: Object) {
      localState.isLoading = false;
      localState.responseMessage = t('general:messages.form_error');
      localState.showResponseMessage = true;
      localState.hasSuccess = false;
      localState.hasError = true;
    },
  };

  return new SingleAssetFormEdit(
    { fields },
    {
      hooks,
      name: assetData.owned
        ? `${t('general:actions.edit_owned_asset')} - ${assetData.asset_number}`
        : `${t('general:actions.edit_rented_asset')} - ${assetData.asset_number}`,
    }
  );
};

/**
 * Returns the CaaS fields ready to be sent to the create/edit endpoint.
 */
function getCaaSFields(form, assetData): AssetCaaSFields {
  const values = form.values().caas;
  const caasFields: AssetCaaSFields = {
    is_caas_asset: values.is_caas_asset || false,
    calibration_service: null,
  };

  if (values.is_caas_asset) {
    // asset_id is only added if the current asset
    // already has a linked Calibration Service.
    const caasAssetID = assetData?.calibration_service?.asset_id
      ? { asset_id: assetData.calibration_service.asset_id }
      : {};

    caasFields.calibration_service = {
      ...caasAssetID,
      type: values.type || null,
      price: values.price || null,
      vendor: values.vendor || null,
      currency: values.currency || null,
      observations: values.observations || null,
      is_rental_backfill: values.is_rental_backfill,
    };
  }

  return caasFields;
}
